import type { IBasePathAnalyticPageWithParent } from '~/modules/path-analysis/types';
import type {
  IDemoDailySeries,
  IDemoDonutMetric,
  IDemoMetric,
  IDemoOrderRow,
} from '../types/page-analytic-demo';
import type { IAnalyticsData, IPathAnalysisConfig } from '@gem-sdk/analytics';
import type { IPAColumnTypes } from '../types/page-analytic-v2';
import type { RawMetricRow } from '../helpers/pageDetailV2';

import {
  EAnalyticMode as ESdkAnalyticMode,
  EMetricKey as ESdkMetricKey,
} from '@gem-sdk/analytics/types';
import { TARGET_CHANNEL, TARGET_DEVICES, TARGET_VISITOR } from '@gem-sdk/analytics';
import { EAnalyticMode } from '~/modules/analytics/types';
import { EAnalyticColumnKey } from '~/core/gemxql/types';
import { EMetricKey } from '~/types/metric';
import noop from 'lodash/noop';

// The design's sample data: one product page, Sep 20 – Oct 19, 2026, vs the 30 days before. Its
// traffic groups map to GemX channels: Facebook/Instagram → paid social, Google Ads → paid
// search, Other UTM → email.

export const DEMO_DATE_FORMAT = 'YYYY-MM-DD';
export const DEMO_PERIODS = {
  current: { from: '2026-09-20', to: '2026-10-19' },
  previous: { from: '2026-08-21', to: '2026-09-19' },
} as const;
/** Alias of a picked range in the date picker, which then shows the range itself. */
export const DEMO_DATE_FILTER_ALIAS = 'custom';
export const DEMO_DATE_FILTER_TITLE = 'Custom';

/**
 * The demo filter row's controls: Polaris buttons, and `GClickable` activators (a div with
 * role="button", e.g. View by). Clicking one, or pressing an activation key on it, shows the
 * demo toast instead.
 */
export const DEMO_FILTER_BAR_BLOCKED_SELECTOR = 'button, [role="button"]';
export const DEMO_FILTER_BAR_ACTIVATION_KEYS: readonly string[] = ['Enter', ' '];

export const DEMO_TOTALS_ROW: RawMetricRow = {
  [EMetricKey.SESSION]: 7082,
  [EMetricKey.VISITORS]: 6968,
  [EMetricKey.BOUNCE_RATE]: 49.22,
  [EMetricKey.CTR]: 66.03,
  [EMetricKey.AVG_TIME_ON_PAGE]: 131,
  [EMetricKey.PAGE_VIEWS]: 8717,
  [EMetricKey.RPV]: 7.51,
  [EMetricKey.CART_ADDITION]: 1625,
  [EMetricKey.REACHED_CHECKOUT]: 1138,
  [EMetricKey.COMPLETE_CHECKOUT]: 991,
  [EMetricKey.CONVERSION_RATE]: 13.99,
  [EMetricKey.AOV]: 52.61,
  [EMetricKey.REVENUE]: 52344.82,
  [EMetricKey.ADDED_TO_CART]: 3708,
  [EMetricKey.ADD_TO_CART_RATE]: 22.95,
  [EMetricKey.ORDERS]: 995,
  [EAnalyticColumnKey.VISITOR_ITEMS]: [
    { type: 'new', total: 5352 },
    { type: 'returning', total: 1616 },
  ],
  [EAnalyticColumnKey.DEVICE_ITEMS]: [
    { type: 'mobile', total: 6421 },
    { type: 'desktop', total: 587 },
    { type: 'tablet', total: 74 },
  ],
  [EAnalyticColumnKey.TRAFFIC_SOURCE_ITEMS]: [
    { type: 'paid-social', total: 4126 },
    { type: 'paid-search', total: 1323 },
    { type: 'referral', total: 829 },
    { type: 'direct', total: 359 },
    { type: 'email', total: 312 },
    { type: 'organic-search', total: 133 },
  ],
};

export const DEMO_COMPARISON_TOTALS_ROW: RawMetricRow = {
  [EMetricKey.SESSION]: 13205,
  [EMetricKey.VISITORS]: 12969,
  [EMetricKey.BOUNCE_RATE]: 60.63,
  [EMetricKey.CTR]: 55.18,
  [EMetricKey.AVG_TIME_ON_PAGE]: 130.3,
  [EMetricKey.PAGE_VIEWS]: 15868,
  [EMetricKey.RPV]: 5.06,
  [EMetricKey.CART_ADDITION]: 2085,
  [EMetricKey.REACHED_CHECKOUT]: 1426,
  [EMetricKey.COMPLETE_CHECKOUT]: 1273,
  [EMetricKey.CONVERSION_RATE]: 9.64,
  [EMetricKey.AOV]: 51.36,
  [EMetricKey.REVENUE]: 65585.04,
  [EMetricKey.ADDED_TO_CART]: 3740,
  [EMetricKey.ADD_TO_CART_RATE]: 15.79,
  [EMetricKey.ORDERS]: 1277,
  [EAnalyticColumnKey.VISITOR_ITEMS]: [
    { type: 'new', total: 10119 },
    { type: 'returning', total: 2850 },
  ],
  [EAnalyticColumnKey.DEVICE_ITEMS]: [
    { type: 'mobile', total: 12363 },
    { type: 'desktop', total: 711 },
    { type: 'tablet', total: 131 },
  ],
  [EAnalyticColumnKey.TRAFFIC_SOURCE_ITEMS]: [
    { type: 'paid-social', total: 9046 },
    { type: 'paid-search', total: 1570 },
    { type: 'referral', total: 1189 },
    { type: 'direct', total: 617 },
    { type: 'email', total: 614 },
    { type: 'organic-search', total: 169 },
  ],
};

export const DEMO_COLUMN_TYPES: IPAColumnTypes = {
  [EMetricKey.SESSION]: 'INTEGER',
  [EMetricKey.VISITORS]: 'INTEGER',
  [EMetricKey.BOUNCE_RATE]: 'PERCENT',
  [EMetricKey.CTR]: 'PERCENT',
  [EMetricKey.AVG_TIME_ON_PAGE]: 'DURATION',
  [EMetricKey.PAGE_VIEWS]: 'INTEGER',
  [EMetricKey.RPV]: 'CURRENCY',
  [EMetricKey.CART_ADDITION]: 'INTEGER',
  [EMetricKey.REACHED_CHECKOUT]: 'INTEGER',
  [EMetricKey.COMPLETE_CHECKOUT]: 'INTEGER',
  [EMetricKey.CONVERSION_RATE]: 'PERCENT',
  [EMetricKey.AOV]: 'CURRENCY',
  [EMetricKey.REVENUE]: 'CURRENCY',
  [EMetricKey.ADDED_TO_CART]: 'INTEGER',
  [EMetricKey.ADD_TO_CART_RATE]: 'PERCENT',
  [EMetricKey.ORDERS]: 'INTEGER',
};

// Oldest day first.
export const DEMO_DAILY_SERIES: Record<string, IDemoDailySeries> = {
  [EMetricKey.SESSION]: {
    current: [
      241, 210, 196, 138, 262, 218, 308, 208, 181, 145, 196, 175, 274, 232, 246, 226, 205, 186, 195,
      201, 291, 319, 255, 300, 226, 299, 266, 297, 292, 294,
    ],
    previous: [
      404, 606, 406, 498, 507, 465, 566, 476, 561, 636, 570, 600, 648, 583, 633, 633, 580, 632, 361,
      208, 211, 224, 311, 294, 344, 267, 258, 265, 276, 182,
    ],
  },
  [EMetricKey.VISITORS]: {
    current: [
      240, 210, 193, 136, 259, 213, 304, 206, 175, 142, 189, 172, 271, 227, 244, 221, 198, 182, 192,
      197, 289, 308, 252, 299, 226, 290, 263, 294, 287, 289,
    ],
    previous: [
      394, 589, 393, 488, 504, 459, 561, 467, 549, 622, 556, 592, 641, 571, 620, 626, 573, 626, 352,
      206, 207, 223, 304, 292, 341, 263, 253, 259, 259, 179,
    ],
  },
  [EMetricKey.BOUNCE_RATE]: {
    current: [
      48.55, 49.52, 48.47, 49.28, 58.02, 50.46, 54.22, 49.04, 46.96, 40, 51.02, 41.71, 46.35, 43.97,
      41.87, 45.13, 45.85, 41.94, 47.18, 40.3, 42.96, 49.53, 52.55, 56, 50.44, 51.84, 50.38, 56.9,
      56.51, 52.38,
    ],
    previous: [
      54.95, 61.55, 50, 63.45, 59.57, 58.92, 65.55, 64.71, 62.92, 70.13, 70.18, 65.33, 60.49, 59.01,
      58.29, 61.45, 66.21, 63.29, 61.5, 58.17, 64.93, 61.61, 60.45, 57.48, 56.69, 52.81, 47.29,
      41.51, 52.9, 43.41,
    ],
  },
  [EMetricKey.CTR]: {
    current: [
      64.19, 69.92, 66.96, 80.12, 64.59, 70.04, 64.25, 64.46, 62.98, 71.68, 60.16, 64.04, 68.91,
      70.81, 68.99, 65.29, 74.9, 73.12, 61.32, 67.72, 69.4, 63.93, 63.55, 63.46, 61.34, 62.5, 66.28,
      60.73, 61.35, 66.12,
    ],
    previous: [
      57.37, 55.28, 56.14, 47.79, 50.08, 52.21, 47.99, 47.38, 42.9, 43.01, 44.02, 48.06, 53.19,
      55.54, 59.31, 61.66, 51.01, 53.73, 59.3, 72.98, 64.41, 67.43, 61.74, 67.62, 68.29, 68.51,
      69.18, 68.97, 65.05, 72.32,
    ],
  },
  [EMetricKey.AVG_TIME_ON_PAGE]: {
    current: [
      119.7, 166.4, 129.5, 164.4, 140.1, 117, 165.2, 145.6, 112.6, 110.5, 122.1, 113, 132.8, 122.2,
      129.5, 152.7, 141.5, 122.7, 120.4, 168.1, 82.1, 88.6, 146.4, 126.5, 129.9, 125.2, 133.1,
      169.4, 134, 136.9,
    ],
    previous: [
      158.5, 116.3, 116.6, 95.6, 92.1, 129.8, 135.9, 138.2, 140.7, 140.9, 145.7, 133.8, 129.5,
      149.8, 129.6, 129.7, 133.3, 107.2, 120.5, 74.1, 118.1, 141.8, 131.8, 168.4, 121, 150.4, 155.2,
      116.6, 206.1, 126.8,
    ],
  },
  [EMetricKey.PAGE_VIEWS]: {
    current: [
      296, 256, 230, 166, 305, 257, 358, 242, 208, 173, 251, 228, 341, 298, 316, 291, 247, 253, 243,
      254, 366, 402, 299, 364, 269, 376, 341, 354, 370, 363,
    ],
    previous: [
      495, 729, 529, 588, 639, 565, 673, 572, 669, 751, 652, 720, 784, 704, 757, 793, 694, 750, 457,
      248, 236, 261, 345, 349, 410, 308, 318, 319, 329, 224,
    ],
  },
  [EMetricKey.RPV]: {
    current: [
      6.35, 9.41, 8.89, 8.94, 5.79, 5.77, 8.23, 4.97, 10.9, 5.62, 7.79, 8.73, 8.6, 5.98, 10.72,
      6.41, 9.67, 9.58, 8.43, 9.88, 9.19, 5.63, 7.43, 5.49, 7.36, 5.82, 9.44, 6.54, 5.86, 5.87,
    ],
    previous: [
      6.21, 3.73, 7.05, 5.91, 5.73, 4.53, 4.33, 4.45, 4.59, 4.41, 4.39, 4.13, 5.77, 5.86, 3.58,
      3.56, 4.61, 5.74, 5.02, 5.64, 4.07, 3.88, 4.62, 4.6, 5.55, 8.33, 9.2, 6.31, 5.54, 5.92,
    ],
  },
  [EMetricKey.CART_ADDITION]: {
    current: [
      56, 48, 36, 36, 58, 47, 61, 44, 42, 24, 42, 48, 57, 60, 66, 48, 44, 47, 53, 60, 80, 74, 50,
      61, 58, 76, 73, 54, 61, 61,
    ],
    previous: [
      71, 92, 78, 89, 82, 74, 87, 64, 85, 74, 73, 71, 99, 100, 99, 87, 78, 90, 69, 43, 28, 38, 45,
      53, 56, 55, 74, 46, 50, 35,
    ],
  },
  [EMetricKey.REACHED_CHECKOUT]: {
    current: [
      41, 33, 24, 22, 38, 28, 38, 29, 31, 15, 32, 37, 40, 37, 52, 31, 32, 31, 37, 44, 56, 54, 36,
      38, 40, 48, 64, 43, 44, 43,
    ],
    previous: [
      41, 61, 50, 53, 57, 48, 63, 39, 56, 59, 55, 56, 78, 75, 62, 49, 58, 66, 51, 28, 15, 23, 29,
      34, 38, 39, 59, 31, 33, 20,
    ],
  },
  [EMetricKey.COMPLETE_CHECKOUT]: {
    current: [
      34, 31, 26, 20, 34, 25, 40, 25, 31, 14, 27, 31, 41, 29, 49, 27, 34, 33, 31, 44, 50, 36, 36,
      28, 24, 38, 47, 38, 32, 36,
    ],
    previous: [
      40, 44, 45, 50, 49, 33, 55, 40, 44, 54, 56, 47, 70, 64, 55, 41, 48, 69, 46, 27, 19, 15, 26,
      29, 36, 40, 45, 37, 29, 20,
    ],
  },
  [EMetricKey.CONVERSION_RATE]: {
    current: [
      14.11, 14.76, 13.27, 14.49, 12.98, 11.47, 12.99, 12.02, 17.13, 9.66, 13.78, 17.71, 14.96,
      12.5, 19.92, 11.95, 16.59, 17.74, 15.9, 21.89, 17.18, 11.29, 14.12, 9.33, 10.62, 12.71, 17.67,
      12.79, 10.96, 12.24,
    ],
    previous: [
      9.9, 7.26, 11.08, 10.04, 9.66, 7.1, 9.72, 8.4, 7.84, 8.49, 9.82, 7.83, 10.8, 10.98, 8.69,
      6.48, 8.28, 10.92, 12.74, 12.98, 9, 6.7, 8.36, 9.86, 10.47, 14.98, 17.44, 13.96, 10.51, 10.99,
    ],
  },
  [EMetricKey.AOV]: {
    current: [
      44.79, 63.76, 63.52, 60.82, 44.11, 49.15, 62.54, 40.98, 61.54, 56.96, 54.51, 48.42, 56.85,
      46.82, 53.36, 52.49, 56.32, 52.85, 52.18, 44.22, 53.13, 48.14, 51.99, 58.65, 66.52, 44.39,
      52.85, 49.29, 52.55, 45.88,
    ],
    previous: [
      61.16, 49.96, 60.26, 57.69, 58.89, 62.98, 44.21, 52, 57.29, 50.81, 43.63, 52.07, 52.87, 52.28,
      40.39, 54.41, 55, 52.11, 38.38, 43, 44.36, 54.05, 54.06, 46.27, 52.61, 54.76, 50.58, 44.16,
      47.79, 53.01,
    ],
  },
  [EMetricKey.REVENUE]: {
    current: [
      1522.99, 1976.55, 1714.94, 1216.34, 1499.66, 1228.87, 2501.49, 1024.6, 1907.88, 797.4,
      1471.77, 1500.95, 2330.66, 1357.81, 2614.56, 1417.24, 1914.78, 1744.16, 1617.66, 1945.88,
      2656.56, 1733.13, 1871.71, 1642.18, 1663.07, 1686.84, 2483.8, 1922.16, 1681.49, 1697.69,
    ],
    previous: [
      2446.52, 2198.36, 2771.97, 2884.3, 2885.42, 2078.3, 2431.36, 2079.99, 2520.61, 2743.63,
      2443.37, 2447.1, 3700.63, 3345.65, 2221.47, 2230.8, 2639.89, 3595.32, 1765.51, 1160.99,
      842.75, 864.76, 1405.46, 1341.82, 1894.11, 2190.26, 2326.83, 1633.93, 1433.68, 1060.25,
    ],
  },
  [EMetricKey.ADDED_TO_CART]: {
    current: [
      100, 76, 62, 70, 127, 106, 135, 104, 88, 57, 94, 130, 133, 175, 139, 119, 99, 126, 110, 126,
      181, 182, 116, 140, 135, 179, 185, 120, 151, 143,
    ],
    previous: [
      126, 167, 175, 159, 181, 144, 153, 115, 144, 122, 119, 138, 196, 160, 168, 180, 140, 146, 118,
      60, 37, 61, 75, 94, 96, 98, 137, 87, 89, 55,
    ],
  },
  [EMetricKey.ADD_TO_CART_RATE]: {
    current: [
      23.24, 22.86, 18.37, 26.09, 22.14, 21.56, 19.81, 21.15, 23.2, 16.55, 21.43, 27.43, 20.8,
      25.86, 26.83, 21.24, 21.46, 25.27, 27.18, 29.85, 27.49, 23.2, 19.61, 20.33, 25.66, 25.42,
      27.44, 18.18, 20.89, 20.75,
    ],
    previous: [
      17.57, 15.18, 19.21, 17.87, 16.17, 15.91, 15.37, 13.45, 15.15, 11.64, 12.81, 11.83, 15.28,
      17.15, 15.64, 13.74, 13.45, 14.24, 19.11, 20.67, 13.27, 16.96, 14.47, 18.03, 16.28, 20.6,
      28.68, 17.36, 18.12, 19.23,
    ],
  },
  [EMetricKey.ORDERS]: {
    current: [
      34, 31, 27, 20, 34, 25, 40, 25, 31, 14, 27, 31, 41, 29, 49, 27, 34, 33, 31, 44, 50, 36, 36,
      28, 25, 38, 47, 39, 32, 37,
    ],
    previous: [
      40, 44, 46, 50, 49, 33, 55, 40, 44, 54, 56, 47, 70, 64, 55, 41, 48, 69, 46, 27, 19, 16, 26,
      29, 36, 40, 46, 37, 30, 20,
    ],
  },
};

// ─── Path analysis ───────────────────────────────────────────────────────────

// Only the sample page itself: the data has no pages visited after it.
const DEMO_PATH_PAGE = {
  id: 'a0873b1c39c455f0',
  parentID: '',
  level: 1,
  pageTitle: 'Sample page',
  pageType: 'product',
  locationPath: '/products/formula-49',
};

const DEMO_CURRENT_PATH: IBasePathAnalyticPageWithParent = {
  ...DEMO_PATH_PAGE,
  metric: {
    totalSessions: 7082,
    totalSessionsWithoutAction: 3486,
    totalPageViewed: 8717,
    totalClicked: 5756,
    totalClickedHasHref: 5756,
    totalAddedToCart: 3708,
    totalReachedCheckout: 1138,
    totalContinuationSession: 3181,
    totalOrders: 995,
    totalVisitors: 6968,
    totalSessionsWithCartAdditions: 1625,
    totalSessionsThatReachedCheckout: 1138,
    totalSessionsThatCompletedCheckout: 991,
  },
};

const DEMO_PREVIOUS_PATH: IBasePathAnalyticPageWithParent = {
  ...DEMO_PATH_PAGE,
  metric: {
    totalSessions: 13205,
    totalSessionsWithoutAction: 8006,
    totalPageViewed: 15868,
    totalClicked: 8756,
    totalClickedHasHref: 8756,
    totalAddedToCart: 3740,
    totalReachedCheckout: 1426,
    totalContinuationSession: 4966,
    totalOrders: 1277,
    totalVisitors: 12969,
    totalSessionsWithCartAdditions: 2085,
    totalSessionsThatReachedCheckout: 1426,
    totalSessionsThatCompletedCheckout: 1273,
  },
};

export const DEMO_PATH_ANALYSIS_DATA: IAnalyticsData = {
  isLoading: false,
  locationPath: DEMO_PATH_PAGE.locationPath,
  pathAnalytics: [DEMO_CURRENT_PATH],
  previousPathAnalyticsMap: { [DEMO_PREVIOUS_PATH.id]: DEMO_PREVIOUS_PATH },
};

/** As in the builder's demo: path analysis unlocked, heatmap off (it needs a real page). */
export const DEMO_PATH_ANALYSIS_CONFIG: IPathAnalysisConfig = {
  pricing: { isLoading: false, isPathAnalysisEnabled: true, onOpenModalPricing: noop },
  heatmap: { isEnabledV1: false, isEnabledV2: false, onView: noop },
};

// ─── Metric cards (same metrics and titles as the real page detail) ─────────

export const DEMO_OVERVIEW_METRICS: IDemoMetric[] = [
  { key: ESdkMetricKey.SESSION, title: 'Sessions' },
  { key: ESdkMetricKey.VISITORS, title: 'Visitors' },
  { key: ESdkMetricKey.BOUNCE_RATE, title: 'Bounce Rate' },
  { key: ESdkMetricKey.CTR, title: 'Click-through rate' },
];

export const DEMO_ENGAGEMENT_METRICS: IDemoMetric[] = [
  { key: ESdkMetricKey.AVG_TIME_ON_PAGE, title: 'Avg time on page' },
  { key: ESdkMetricKey.PAGE_VIEWS, title: 'Pageviews' },
  { key: ESdkMetricKey.RPV, title: 'Revenue per visitor' },
];

export const DEMO_BREAKDOWN_METRICS: IDemoDonutMetric[] = [
  { key: ESdkMetricKey.VISITOR_ITEMS, title: 'Visitors by type', targets: TARGET_VISITOR },
  { key: ESdkMetricKey.DEVICE_ITEMS, title: 'Sessions by device type', targets: TARGET_DEVICES },
  {
    key: ESdkMetricKey.TRAFFIC_SOURCE_ITEMS,
    title: 'Sessions by traffic source',
    targets: TARGET_CHANNEL,
    isSorted: true,
  },
];

export const DEMO_CHECKOUT_FUNNEL_METRICS: IDemoMetric[] = [
  { key: ESdkMetricKey.CART_ADDITION, title: 'Sessions with cart additions' },
  { key: ESdkMetricKey.REACHED_CHECKOUT, title: 'Sessions that reached checkout' },
  { key: ESdkMetricKey.COMPLETE_CHECKOUT, title: 'Sessions that completed checkout' },
];

export const DEMO_SALES_METRICS: IDemoMetric[] = [
  { key: ESdkMetricKey.CONVERSION_RATE, title: 'Conversion Rate' },
  { key: ESdkMetricKey.AOV, title: 'AOV' },
  { key: ESdkMetricKey.REVENUE, title: 'Revenue' },
];

export const DEMO_CART_METRICS: IDemoMetric[] = [
  { key: ESdkMetricKey.ADDED_TO_CART, title: 'Add to cart' },
  { key: ESdkMetricKey.ADD_TO_CART_RATE, title: 'Add to cart rate' },
  { key: ESdkMetricKey.ORDERS, title: 'Orders' },
];

// The sample data is All Session only, so the demo shows that mode whatever the shop saved.
export const DEMO_ANALYTIC_MODE = EAnalyticMode.ALL_SESSION;
export const DEMO_SDK_ANALYTIC_MODE = ESdkAnalyticMode.ALL_SESSION;

/** Three cards per row on large screens, as on the real page detail. */
export const DEMO_GRID_THIRD_SPAN = { xs: 6, lg: 4 } as const;
export const DEMO_GRID_HALF_SPAN = { xs: 6, lg: 6 } as const;
export const DEMO_GRID_GAP = { xs: '200' } as const;
/**
 * The design keeps 16px each side of the 1104px report column; Polaris Page pads 24px. Under its
 * sm breakpoint Polaris pads the title instead (the page has none there), which would add up to
 * 32px, so the header Box — the page's first child — drops its own.
 */
export const DEMO_PAGE_GUTTER_CLASS =
  '[&_.Polaris-Page]:px-[var(--p-space-400)] [&_.Polaris-Page>.Polaris-Box:first-child]:[padding-inline:0]';

// ─── Loading skeleton ────────────────────────────────────────────────────────

/** The demo page's first screen while it loads, sized like the controls and cards it stands for. */
export const DEMO_SKELETON = {
  BANNER_HEIGHT: '36px',
  CONTROL_HEIGHT: '32px',
  // Date range, compare range and currency.
  FILTER_WIDTHS: ['165px', '225px', '125px'],
  VIEW_BY_WIDTH: '168px',
  TOGGLE_WIDTH: '32px',
  TITLE_WIDTH: '20%',
  METRIC_KEYS: [0, 1, 2, 3],
  METRIC_COLUMNS: { xs: 2, md: 4 },
  OVERVIEW_CHART_HEIGHT: '280px',
  JOURNEY_CHART_HEIGHT: '240px',
} as const;

// ─── Latest orders ───────────────────────────────────────────────────────────

// Orders placed after viewing the page, and all the store's orders, Sep 20 – Oct 19, 2026.
export const DEMO_TOTAL_ORDERS = 995;
export const DEMO_TOTAL_STORE_ORDERS = 4798;

/** Item fields the data has no value for. No image: the SDK's thumbnail falls back to its own. */
export const DEMO_ORDER_ITEM_BASE = {
  image_url: '',
  price: '',
  product_id: '',
  product_variant_id: '',
  properties: '',
  sku: '',
};

/** The data's latest orders from the page, newest first. Times in UTC. */
export const DEMO_ORDER_ROWS: IDemoOrderRow[] = [
  {
    orderId: '18960548987043',
    date: '2026-10-19T23:54:43Z',
    totalPrice: 42.5,
    trafficSource: 'Email',
    device: 'Mobile',
    visitorType: 'New',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Rugged / 4oz', quantity: 1 }],
  },
  {
    orderId: '18960539222179',
    date: '2026-10-19T23:47:21Z',
    totalPrice: 50,
    trafficSource: 'Paid social',
    device: 'Mobile',
    visitorType: 'New',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Original / 4oz', quantity: 1 }],
  },
  {
    orderId: '18960537747619',
    date: '2026-10-19T23:46:23Z',
    totalPrice: 34.29,
    trafficSource: 'Paid social',
    device: 'Mobile',
    visitorType: 'New',
    items: [
      { productTitle: 'Oil Based Pomade', variantTitle: 'Original', quantity: 1 },
      { productTitle: 'Order Protect', variantTitle: '', quantity: 1 },
      { productTitle: 'Formula 49', variantTitle: 'Original / 2oz', quantity: 1 },
    ],
  },
  {
    orderId: '18960511008931',
    date: '2026-10-19T23:25:25Z',
    totalPrice: 100,
    trafficSource: 'Referral',
    device: 'Mobile',
    visitorType: 'New',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Original / 4oz', quantity: 2 }],
  },
  {
    orderId: '18960360603811',
    date: '2026-10-19T21:42:55Z',
    totalPrice: 75,
    trafficSource: 'Paid search',
    device: 'Mobile',
    visitorType: 'New',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Original / 2oz', quantity: 3 }],
  },
  {
    orderId: '18960303358115',
    date: '2026-10-19T21:05:15Z',
    totalPrice: 28.98,
    trafficSource: 'Referral',
    device: 'Mobile',
    visitorType: 'Returning',
    items: [
      { productTitle: "Punkin' Slicks Formula 49", variantTitle: '2oz', quantity: 1 },
      { productTitle: 'Order Protect', variantTitle: '', quantity: 1 },
    ],
  },
  {
    orderId: '18960294969507',
    date: '2026-10-19T20:59:43Z',
    totalPrice: 25,
    trafficSource: 'Paid social',
    device: 'Mobile',
    visitorType: 'New',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Rugged / 2oz', quantity: 2 }],
  },
  {
    orderId: '18960286810275',
    date: '2026-10-19T20:54:19Z',
    totalPrice: 110.98,
    trafficSource: 'Paid search',
    device: 'Mobile',
    visitorType: 'New',
    items: [
      { productTitle: 'Shave Soap', variantTitle: 'Rugged / 8oz', quantity: 1 },
      { productTitle: 'Beast Bar', variantTitle: 'Rugged', quantity: 3 },
      { productTitle: 'Formula 49', variantTitle: 'Rugged / 2oz', quantity: 1 },
      { productTitle: 'Order Protect', variantTitle: '', quantity: 1 },
      { productTitle: 'Johnny Slicks Deodorant', variantTitle: 'Rugged / Single', quantity: 1 },
    ],
  },
  {
    orderId: '18960263282851',
    date: '2026-10-19T20:39:12Z',
    totalPrice: 35,
    trafficSource: 'Paid social',
    device: 'Mobile',
    visitorType: 'Returning',
    items: [{ productTitle: 'Formula 49', variantTitle: 'Rugged / 4oz', quantity: 1 }],
  },
  {
    orderId: '18960172744867',
    date: '2026-10-19T19:43:44Z',
    totalPrice: 128.69,
    trafficSource: 'Referral',
    device: 'Desktop',
    visitorType: 'New',
    items: [
      { productTitle: 'Oil Based Pomade', variantTitle: 'Original', quantity: 1 },
      { productTitle: 'Body Wash', variantTitle: 'Original', quantity: 2 },
      { productTitle: 'Beast Wash Shower Set', variantTitle: 'Original-Liquid', quantity: 1 },
      { productTitle: 'Johnny Slicks Deodorant', variantTitle: 'Original / Single', quantity: 1 },
      { productTitle: 'Order Protect', variantTitle: '', quantity: 1 },
    ],
  },
];
